import { useState, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  variant: "default" | "danger";
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  loading?: boolean;
  children?: ReactNode;
}

/**
 * ConfirmationDialog - A modal dialog for confirming admin actions.
 *
 * Features:
 * - Optional reason input for audit logging
 * - Danger variant for destructive actions
 * - Loading state during async operations
 * - Focus trap and escape key handling
 */
export function ConfirmationDialog({
  isOpen,
  title,
  description,
  confirmLabel,
  variant,
  onConfirm,
  onCancel,
  requireReason = false,
  reasonLabel = "Reason",
  reasonPlaceholder = "Enter reason for this action...",
  loading = false,
  children,
}: ConfirmationDialogProps) {
  const [reason, setReason] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Focus input or dialog when opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (requireReason && inputRef.current) {
          inputRef.current.focus();
        } else if (dialogRef.current) {
          dialogRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, requireReason]);

  // Handle escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen && !loading) {
        onCancel();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, loading, onCancel]);

  // Reset reason when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setReason("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canConfirm = !requireReason || reason.trim().length > 0;

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm(requireReason ? reason.trim() : undefined);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onCancel();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        tabIndex={-1}
        className="
          relative z-10
          w-full max-w-md
          bg-white rounded-xl shadow-2xl
          animate-[dialogIn_200ms_ease-out]
        "
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2
            id="dialog-title"
            className="text-lg font-semibold text-gray-900"
          >
            {title}
          </h2>
          <p
            id="dialog-description"
            className="mt-2 text-sm text-gray-600"
          >
            {description}
          </p>
        </div>

        {/* Optional children (e.g., additional form fields) */}
        {children && (
          <div className="px-6 pb-4">
            {children}
          </div>
        )}

        {/* Reason input */}
        {requireReason && (
          <div className="px-6 pb-4">
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              {reasonLabel}
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              maxLength={500}
              disabled={loading}
              className="
                w-full px-3 py-2
                text-sm text-gray-900
                border border-gray-300 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-magic-500 focus:border-transparent
                disabled:bg-gray-50 disabled:text-gray-500
                resize-none
              "
            />
            <p className="mt-1 text-xs text-gray-500 text-right">
              {reason.length}/500
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="
              px-4 py-2 text-sm font-medium text-gray-700
              bg-white border border-gray-300 rounded-lg
              hover:bg-gray-50
              focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || !canConfirm}
            className={`
              px-4 py-2 text-sm font-medium text-white rounded-lg
              focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              inline-flex items-center gap-2
              ${
                variant === "danger"
                  ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
                  : "bg-magic-600 hover:bg-magic-700 focus-visible:ring-magic-500"
              }
            `}
          >
            {loading && <LoadingSpinner className="w-4 h-4" />}
            {confirmLabel}
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes dialogIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>,
    document.body
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export default ConfirmationDialog;
